/**
 * Module dependencies.
 */

import { AnimationDelayProvider } from 'components/providers/animation-delay-provider/animation-delay-provider';
import { Catalog } from 'components/pages/catalog/catalog';
import { Contacts } from 'components/pages/contacts/contacts';
import {
  GetStaticPathsResult,
  GetStaticProps,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next';

import { I18nStoreProps, withStaticI18n } from 'server/utils/with-i18n';
import { Language } from 'types/language';
import { Navbar } from 'components/molecules/navbar/navbar';
import { Portfolio } from 'components/pages/portfolio/portfolio';
import { SSRConfig, useTranslation } from 'next-i18next';
import {
  catalogStaticProps,
  resolveCatalogPaths,
} from 'constants/static-props/catalog-static-props';

import { fetchGraphql } from 'utils/fetch-graphql';
import {
  optimizeStaticProps,
  pageComponents,
  pageStaticProps,
} from 'constants/page-components';
import { resolvePathParts } from 'utils/path-segments';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Home from '../components/pages/home/home';
import getConfig from 'next/config';

/**
 * `PageParams` type.
 */

type PageParams = {
  path?: Array<string>;
};

/**
 * `Props` type.
 */

type Props = {
  config: any;
  languages: Language[];
  path: string;
  title: string;
  validPath: boolean;
} & SSRConfig;

/**
 * Export `namespaces`.
 */

export const namespaces = ['translation'];

/**
 * Get languages.
 */

async function getLanguages(): Promise<Language[]> {
  const result = await fetchGraphql(`
      query {
        languages {
          code
          name
          icon {
            width
            height
            title
            type
            filename_download
            filename_disk
          }
        }
      }
    `);

  return result.languages;
}

/**
 * Export `getStaticPaths`.
 */

export async function getStaticPaths({
  defaultLocale,
  locales,
}: {
  defaultLocale: string;
  locales: string[];
}): Promise<GetStaticPathsResult<PageParams>> {
  const catalog = await catalogStaticProps();
  const catalogPaths = resolveCatalogPaths(
    '/catalog',
    catalog.catalogCategories,
    locales,
  );

  const catalogPathsWithLocale = locales.flatMap((locale) => {
    return catalogPaths.map((path) => ({
      locale,
      params: { path: path.split('/').filter(Boolean) },
    }));
  });

  const pathsWithLocale = locales.flatMap((locale) =>
    Object.values(pageComponents).map((path) => ({
      locale,
      params: { path: path.split('/').filter(Boolean) },
    })),
  );

  const pathsWithoutLocale = Object.values(pageComponents).map((path) => ({
    locale: defaultLocale,
    params: { path: path.split('/').filter(Boolean) },
  }));

  const catalogPathsWithoutLocale = catalogPaths.map((path) => ({
    locale: defaultLocale,
    params: { path: path.split('/').filter(Boolean) },
  }));

  const result = {
    fallback: 'blocking' as const,
    paths: [
      ...pathsWithLocale,
      ...pathsWithoutLocale,
      ...catalogPathsWithLocale,
      ...catalogPathsWithoutLocale,
    ],
  };

  return result;
}

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<I18nStoreProps> =
  withStaticI18n<I18nStoreProps>(namespaces)(async (
    context: GetStaticPropsContext<any>,
  ): Promise<GetStaticPropsResult<Props>> => {
    const path = '/'.concat(context?.params?.path?.join('/') ?? '');
    const { serverRuntimeConfig } = getConfig();
    const languages = await getLanguages();
    const [mainPath, ...subpath] = resolvePathParts(path);

    // eslint-disable-next-line no-console
    console.log(`Reload page: ${path}: ${mainPath} => ${subpath.join(', ')}`);

    const normalizer =
      optimizeStaticProps[mainPath] ?? optimizeStaticProps.default;

    return {
      props: {
        config: serverRuntimeConfig,
        languages,
        path,
        ...normalizer(await pageStaticProps[mainPath]?.(), subpath),
      },
    };
  });

/**
 * `PageContent` component.
 */

function PageContent(props: Props) {
  const { path, ...rest } = props;
  const [mainPath, ...subpath] = resolvePathParts(path);

  if (['/favicon.ico'].includes(path)) {
    return null;
  }

  switch (mainPath) {
    case pageComponents.home:
      return <Home {...(rest as any)} />;

    case pageComponents.contacts:
      return <Contacts {...(rest as any)} />;

    case pageComponents.portfolio:
      return <Portfolio {...(rest as any)} paths={subpath} />;

    case pageComponents.catalog:
      return <Catalog {...(rest as any)} paths={subpath} />;

    default:
      return null;
  }
}

/**
 * Export `Page` component.
 */

export default function Page(props: Props) {
  const { languages, title = '' } = props;
  const [translate] = useTranslation();
  const router = useRouter();

  return (
    <>
      <Head>
        <title>{translate('title')}</title>

        <link
          href={`https://www.perfilideal.com${router.asPath}`}
          rel={'canonical'}
        />

        <meta content={translate('description')} name={'description'} />

        <meta
          content={'width=device-width, initial-scale=1'}
          name={'viewport'}
        />

        <meta content={translate('title')} property={'og:title'} />

        <meta
          content={translate('ogDescription')}
          property={'og:description'}
        />

        <meta content={'/ogimage.png'} property={'og:image'} />

        <meta content={'https://perfilideal.com/'} property={'og:url'} />

        <meta content={'/ogimage.png'} name={'twitter:card'} />

        <link href={'/favicon.ico'} rel={'icon'} type={'image/x-icon'} />
      </Head>

      <Navbar languages={languages} />

      <AnimationDelayProvider>
        <PageContent {...props} />
      </AnimationDelayProvider>
    </>
  );
}
